import { cx } from '@emotion/css';
import { Typography } from 'antd';
import SafeImage from 'components/SafeImage';
import { VideoPreview } from 'components/VideoPreview';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { SupplementKnowledgeAnswer } from 'services/api/supplementKnowledge';
import { getTranslateContent } from './utils';
import { useCallback } from 'react';

interface AnswerCellProps {
  ellipsis?: boolean;
  answers?: SupplementKnowledgeAnswer[];
}

interface RenderAnswerProps {
  ellipsis?: boolean;
  answer: SupplementKnowledgeAnswer;
  textClassName?: string;
}

export const ImageAnswer = (props: RenderAnswerProps) => {
  const { answer } = props;

  if (answer.msgType !== 'IMAGE') return null;

  return <SafeImage preview src={answer.localUrl || answer.url || ''} width={24} height={24} />;
};

export const VideoAnswer = (props: RenderAnswerProps) => {
  const { answer } = props;
  if (answer.msgType !== 'VIDEO') return null;

  return (
    <VideoPreview
      videoUrl={answer.localUrl || answer.url || ''}
      thumbUrl={answer.thumbUrl}
      thumbWidth={24}
      thumbHeight={24}
    />
  );
};

export const AnswerCell = (props: AnswerCellProps) => {
  const { answers, ellipsis = true } = props;
  const language = useSystemLanguageValue();

  const textAnswers = answers?.filter((answer) => answer.msgType === 'TEXT');
  const nonTextAnswers = answers?.filter((answer) => answer.msgType !== 'TEXT');

  const renderAnswer = useCallback(
    (answer: SupplementKnowledgeAnswer) => {
      if (answer.msgType === 'TEXT') {
        const content = getTranslateContent(language, answer.translateMap) || answer.message;
        return (
          <Typography.Paragraph
            className={cx('!m-0 whitespace-pre-wrap', {
              'max-w-[250px]': ellipsis,
            })}
            ellipsis={
              ellipsis
                ? {
                    rows: 3,
                    tooltip: (
                      <div className="max-h-[500px] overflow-y-auto whitespace-pre-wrap">
                        {content}
                      </div>
                    ),
                  }
                : false
            }
          >
            {content}
          </Typography.Paragraph>
        );
      }

      if (answer.msgType === 'IMAGE') {
        return <ImageAnswer answer={answer} ellipsis={ellipsis} />;
      }

      if (answer.msgType === 'VIDEO' && answer.url) {
        return <VideoAnswer answer={answer} />;
      }

      return null;
    },
    [language, ellipsis],
  );

  return (
    <div
      className={cx('flex flex-col gap-1 items-start', {
        'max-w-[250px]': ellipsis,
      })}
    >
      {textAnswers?.map?.((answer) => (
        <div key={answer.messageId || answer.message}>{renderAnswer(answer)}</div>
      ))}
      <div className="flex gap-1">
        {nonTextAnswers?.map?.((answer) => (
          <div key={answer.messageId || answer.message}>{renderAnswer(answer)}</div>
        ))}
      </div>
    </div>
  );
};
